import React, { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type BiblePassageContent,
  type BiblePassageData,
  type ErrorResponse,
  expandUsfmPassageToFullChapter,
  getBibleChooserDataFromString,
  parseBibleViewOptions,
  StandardErrorCodes,
} from '@soulhx/fs-common';
import { useLazyGetPassageQuery } from '@soulhx/client-store';
import { refactorContent } from '../helpers/content-helpers.js';
import { useTranslation } from 'react-i18next';
import { RenderPassage } from '../components/viewer/RenderPassage.js';

export interface UseViewerParams {
  /**
   * Bible version string, in the format abbrev;abbrevLocal;bibleId
   */
  version: string;

  /**
   * Passage, in OSIS format
   */
  passage: string;

  /**
   * Bible viewing options (e.g., words of Christ in red, show/hide footnotes, ...)
   *
   * Defaults provided if string isn't included
   */
  options?: string;

  /**
   * Show the content in read-only mode (defaults to false)
   */
  readOnly?: boolean;

  /**
   * Enables the "show full chapter" button, which can be offputting or
   * can be very helpful. Defaults to false.
   */
  showFullPassageControl?: boolean;

  /**
   * Whether to show the full copyright info right after the pasage
   * (instead of a link); defaults to false
   */
  fullCopyright?: boolean;

  /**
   * Forces hiding the reference, even if `fullCopyright` is off.
   *
   * Still leaves it in the HTML, just hidden, so that exporting
   * HTML functionality in the Editor will still work.
   */
  forceHideRef?: boolean;
}
export const useViewer = ({
  passage,
  version,
  options: optionsString,
  readOnly = false,
  showFullPassageControl = false,
  fullCopyright = false,
  forceHideRef = false,
}: UseViewerParams): { Viewer: JSX.Element; copyright: string[] } => {
  const [nextPassage, setNextPassage] = useState<string | undefined>(passage);
  const [getPassageApi, passageQueryResult] = useLazyGetPassageQuery();
  const [rawContent, setRawContent] = useState<BiblePassageContent[]>([]);
  const [rawParallels, setRawParallels] = useState<BiblePassageData[] | undefined>(undefined);
  const [copyright, setCopyright] = useState<string | undefined>(undefined);
  const { t } = useTranslation(['bibleviewer']);
  const [copyrightList, setCopyrightList] = useState<string[]>([]);
  const options = useMemo(() => parseBibleViewOptions(optionsString), [optionsString]);

  const { content, footnotes, crossReferences, parallels } = useMemo(
    () => refactorContent(options, rawContent, rawParallels),
    [options, rawContent, rawParallels]
  );

  const versionData = useMemo(() => {
    return getBibleChooserDataFromString(version);
  }, [version]);

  const fetchMore = useCallback(
    async (passageIdOverride?: string) => {
      const passageId = passageIdOverride ?? nextPassage;
      if (!passageId) return;

      if (versionData.length < 1 || versionData.some((vd) => vd.bibleId.trim() === '')) {
        return;
      }

      try {
        const apiResponse = await getPassageApi({ bibleId: version, passageId }).unwrap();

        setNextPassage(apiResponse.nextStartingPoint);
        setCopyright(apiResponse.copyright);
        if (apiResponse.copyright) {
          const newCopyright = apiResponse.copyright;
          setCopyrightList((current) => (current.includes(newCopyright) ? current : [...current, newCopyright]));
        }
        setRawContent((current) => current.concat(apiResponse.content));

        if (apiResponse.parallels) {
          setRawParallels((current) => {
            const newParallels: BiblePassageData[] = [];
            apiResponse.parallels!.forEach((p, index) => {
              const existingContent = current?.[index]?.content;
              const newContent = existingContent !== undefined ? existingContent.concat(p.content) : p.content;
              newParallels.push({ ...p, content: newContent });
            });
            return newParallels;
          });
        }
      } catch {
        // failure is tracked reactively via `passageQueryResult.error`, rendered below
      }
    },
    [
      nextPassage,
      versionData,
      version,
      getPassageApi,
      setNextPassage,
      setCopyright,
      setCopyrightList,
      setRawContent,
      setRawParallels,
    ]
  );

  const hasFetchedInitial = useRef(false);
  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    fetchMore();
  }, [fetchMore]);

  const LoadMoreButton = useMemo(() => {
    if (nextPassage && !readOnly) {
      return (
        <div className="bibleviewer-fullchapter-link">
          <button type="button" onClick={() => fetchMore()}>
            {t('bibleviewer:loadMore')}
          </button>
        </div>
      );
    }

    return null;
  }, [nextPassage, readOnly, fetchMore, t]);

  const [hasFetchedFullChapter, setHasFetchedFullChapter] = useState(false);
  const LoadFullChapterButton = useMemo(() => {
    if (nextPassage || hasFetchedFullChapter || !showFullPassageControl) return null;

    const fullChapterString = expandUsfmPassageToFullChapter(passage);
    if (!fullChapterString) return null;

    return (
      <div className="bibleviewer-fullchapter-link">
        <button
          type="button"
          onClick={() => {
            setHasFetchedFullChapter(true);
            setRawContent([]);
            setRawParallels([]);
            setNextPassage(fullChapterString);
            fetchMore(fullChapterString);
          }}
        >
          {t('bibleviewer:loadFullChapter')}
        </button>
      </div>
    );
  }, [
    nextPassage,
    hasFetchedFullChapter,
    passage,
    t,
    setRawContent,
    setRawParallels,
    setNextPassage,
    fetchMore,
    setHasFetchedFullChapter,
    showFullPassageControl,
  ]);

  if (passageQueryResult.isError) {
    const { error } = passageQueryResult;

    const errorCode =
      'data' in error && !!error.data && typeof error.data === 'object' && 'errorCode' in error.data
        ? (error.data as ErrorResponse).errorCode
        : undefined;

    const errorMessage =
      errorCode === StandardErrorCodes.InvalidBibleId
        ? t('bibleviewer:errors.limitExceeded')
        : errorCode === StandardErrorCodes.InvalidBiblePassage
          ? t('bibleviewer:errors.invalidPassage')
          : t('bibleviewer:errors.other');

    return {
      Viewer: <div className="bibleviewer-content-container-error">{errorMessage}</div>,
      copyright: copyrightList,
    };
  }

  if (content.length < 1) {
    return {
      Viewer: <div className="bibleviewer-content-container-nocontent">{t('bibleviewer:errors.noData')}</div>,
      copyright: copyrightList,
    };
  }

  return {
    copyright: copyrightList,
    Viewer: (
      <div className="bibleviewer-content-container">
        <div className="passage-row">
          <RenderPassage
            passage={passage}
            copyright={copyright}
            footnoteTitle={t('bibleviewer:footnotes.title')}
            crossReferences={crossReferences}
            footnotes={footnotes}
            crossRefTitle={t('bibleviewer:footnotes.crossRefTitle')}
            options={options}
            fullCopyright={fullCopyright}
            abbreviationLocal={versionData[0].abbreviationLocal}
            LoadMoreButton={LoadMoreButton ? LoadMoreButton : LoadFullChapterButton ? LoadFullChapterButton : null}
            forceHideRef={forceHideRef}
          >
            {content}
          </RenderPassage>

          {parallels &&
            parallels.map((p, index) => (
              <RenderPassage
                passage={passage}
                key={`${index}-${versionData[index + 1].bibleId}`}
                copyright={p.copyright}
                footnoteTitle={t('bibleviewer:footnotes.title')}
                crossRefTitle={t('bibleviewer:footnotes.crossRefTitle')}
                footnotes={p.footnotes}
                crossReferences={p.crossReferences}
                options={options}
                fullCopyright={fullCopyright}
                abbreviationLocal={versionData[index + 1].abbreviationLocal}
                LoadMoreButton={LoadMoreButton ? LoadMoreButton : LoadFullChapterButton ? LoadFullChapterButton : null}
                forceHideRef={forceHideRef}
              >
                {p.content}
              </RenderPassage>
            ))}
        </div>
      </div>
    ),
  };
};
